import React, { useCallback, useMemo, useRef, useState } from 'react';
import Form, { SimpleItem, ButtonItem } from 'devextreme-react/form';
import dxForm, { InitializedEvent } from 'devextreme/ui/form';
import { ChangePasswordRequest } from '@/api/auth/types';

export type ResetPasswordFormProps = {
	onSubmit?: (data: ChangePasswordRequest) => Promise<void> | void;
	showCurrentPassword?: boolean;
	submitText?: string;
	loading?: boolean;
	colCount?: number;
};

const VALIDATION_GROUP = 'reset-password';

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
	onSubmit,
	showCurrentPassword = true,
	submitText = 'Reset Password',
	loading = false,
	colCount = 1,
}) => {
	const formInstance = useRef<dxForm | undefined>(undefined);
	const saveFormInstance = useCallback((e: InitializedEvent) => {
		formInstance.current = e.component;
	}, []);

	const [submitting, setSubmitting] = useState(false);
	const isBusy = loading || submitting;

	const handleSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			const validation = formInstance.current?.validate?.();
			if (!validation?.isValid) return;

			const fd = formInstance.current?.option('formData') as {
				currentPassword?: string;
				newPassword?: string;
				confirmPassword?: string;
			};

			try {
				setSubmitting(true);
				await onSubmit?.({
					currentPassword: showCurrentPassword ? fd?.currentPassword ?? '' : '',
					newPassword: fd?.newPassword ?? '',
				});

				formInstance.current?.reset();
			} finally {
				setSubmitting(false);
			}
		},
		[onSubmit, showCurrentPassword]
	);

	const passwordRules = useMemo(
		() => [
			{ type: 'required', message: 'New password is required' },
			{ type: 'stringLength', min: 8, message: 'At least 8 characters' },
			{
				type: 'pattern',
				pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
				message: 'Must include upper, lower, and a number',
			},
		],
		[]
	);

	return (
		<form onSubmit={handleSubmit} noValidate>
			<Form
				onInitialized={saveFormInstance}
				colCount={colCount}
				labelLocation="top"
				showColonAfterLabel={false}
				showValidationSummary={true}
				readOnly={false}
				validationGroup={VALIDATION_GROUP}
			>
				<SimpleItem
					dataField="currentPassword"
					label={{ text: 'Current password' }}
					editorType="dxTextBox"
					visible={showCurrentPassword}
					isRequired={showCurrentPassword}
					editorOptions={{
						mode: 'password',
						inputAttr: { autoComplete: 'current-password', name: 'currentPassword' },
						disabled: isBusy,
					}}
					validationRules={
						showCurrentPassword
							? [{ type: 'required', message: 'Current password is required' }]
							: undefined
					}
				/>
				<SimpleItem
					dataField="newPassword"
					label={{ text: 'New password', alignment: 'left' }}
					editorType="dxTextBox"
					editorOptions={{
						mode: 'password',
						inputAttr: { autoComplete: 'new-password', name: 'newPassword' },
						disabled: isBusy,
					}}
					validationRules={passwordRules as any}
				/>
				<SimpleItem
					dataField="confirmPassword"
					label={{ text: 'Confirm new password' }}
					editorType="dxTextBox"
					editorOptions={{
						mode: 'password',
						inputAttr: { autoComplete: 'new-password', name: 'confirmPassword' },
						disabled: isBusy,
					}}
					validationRules={[
						{ type: 'required', message: 'Please confirm your password' },
						{
							type: 'compare',
							comparisonTarget: () =>
								(formInstance.current?.option('formData') as any)?.newPassword,
							message: 'Passwords do not match',
						},
					]}
				/>
				<ButtonItem
					horizontalAlignment="right"
					buttonOptions={{
						text: submitText,
						type: 'default',
						useSubmitBehavior: true,
						disabled: isBusy,
					}}
				/>
			</Form>
		</form>
	);
};

export default ResetPasswordForm;
